import React, { useState, useEffect } from 'react';
import { navLinks, instituteInfo } from '../data/mock';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';
import { Menu, Phone, X } from 'lucide-react';

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      const sections = navLinks.map(link => link.href.substring(1));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    const el = document.getElementById(href.substring(1));
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-x2 shadow-lg ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-lg shadow-slate-900/5 border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); scrollToSection('#home'); }}
            className="flex items-center gap-3 group"
          >
            <img
              src="/logo.png"
              alt="ENIAC Logo"
              className="w-24 h-24 object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(13,148,136,0.8)]"
            />
            <div>
              <span className={`font-bold text-xl tracking-tight transition-colors duration-300 ${scrolled ? 'text-slate-900' : 'text-white'}`}>
                {instituteInfo.name}
              </span>
              <span className={`block text-[10px] font-medium tracking-widest uppercase transition-colors duration-300 ${scrolled ? 'text-teal-600' : 'text-teal-200'}`}>
                Coaching Centre
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.href)}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                  activeSection === link.href.substring(1)
                    ? scrolled ? 'text-teal-700 bg-teal-50' : 'text-white bg-white/15'
                    : scrolled
                      ? 'text-slate-600 hover:text-teal-700 hover:bg-slate-50'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA + Mobile */}
          <div className="flex items-center gap-3">
            <a href={`tel:${instituteInfo.phone}`} className="hidden sm:flex">
              <Button
                variant="outline"
                className={`rounded-full gap-2 font-medium transition-all duration-300 ${
                  scrolled
                    ? 'border-teal-200 text-teal-700 hover:bg-teal-50 hover:border-teal-300'
                    : 'border-white/30 text-white hover:bg-white/10 bg-white/5'
                }`}
              >
                <Phone className="w-4 h-4" />
                Call Now
              </Button>
            </a>

            {/* Mobile menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`lg:hidden rounded-xl ${scrolled ? 'text-slate-700 hover:bg-slate-100 w-21 h-21' : 'text-white hover:bg-white/10'}`}
                >
                  <Menu className="w-21 h-21" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-white border-l border-slate-100">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full pt-8">
                  <div className="flex items-center gap-3 mb-10 px-2">
                    <img
                      src="/logo.png"
                      alt="ENIAC Logo"
                      className="w-16 h-16 object-contain"
                    />
                    <div>
                      <span className="font-bold text-xl text-slate-900">{instituteInfo.name}</span>
                      <span className="block text-[10px] font-medium tracking-widest uppercase text-teal-600">Coaching Centre</span>
                    </div>
                  </div>
                  <nav className="flex flex-col gap-1">
                    {navLinks.map((link) => (
                      <button
                        key={link.id}
                        onClick={() => scrollToSection(link.href)}
                        className={`text-left px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                          activeSection === link.href.substring(1)
                            ? 'text-teal-700 bg-teal-50'
                            : 'text-slate-600 hover:text-teal-700 hover:bg-slate-50'
                        }`}
                      >
                        {link.label}
                      </button>
                    ))}
                  </nav>
                  <div className="mt-auto pb-8 px-2">
                    <a href={`tel:${instituteInfo.phone}`} className="w-full">
                      <Button className="w-full rounded-full bg-teal-600 hover:bg-teal-700 text-white gap-2 h-12 text-base">
                        <Phone className="w-4 h-4" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
